/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode } from 'react';

import cn from 'classnames';

import { Section } from 'ui/molecules/Section/Section';

import styles from './McpExamplePromptsSection.module.scss';

type Prompt = {
  text: ReactNode;
  chain?: string[];
  answer?: string;
};

type Column = {
  key: 'verify' | 'grow' | 'research';
  label: string;
  prompts: Prompt[];
};

const COLUMNS: Column[] = [
  {
    key: 'verify',
    label: 'Verify',
    prompts: [
      {
        text: (
          <>
            Validate company number <span className={styles.chip}>09410808</span> in UK
          </>
        ),
        chain: ['search', 'verify'],
        answer: 'Active · Companies House · registered 2015',
      },
      {
        text: (
          <>
            Check company VAT <span className={styles.chip}>FR92057505539</span>
          </>
        ),
      },
      { text: <>Show me the ownership structure of Unilever</> },
      { text: <>Get financial data for company ticker LSEG for the last 5 years</> },
    ],
  },
  {
    key: 'grow',
    label: 'Grow',
    prompts: [
      {
        text: (
          <>
            Find all active companies in the UK with NACE code <span className={styles.chip}>6311</span>
          </>
        ),
        chain: ['search', 'finance'],
        answer: '1,204 matches · sorted by revenue',
      },
      { text: <>Find the top 50 IT companies in UK by revenue</> },
      { text: <>Find contact information by LinkedIn URL</> },
      { text: <>Show me 50 companies growing 100% yoy by revenue in UK</> },
    ],
  },
  {
    key: 'research',
    label: 'Research',
    prompts: [
      {
        text: <>Show me all subsidiaries of Mastercard</>,
        chain: ['search', 'graph'],
        answer: '38 entities across 19 countries',
      },
      { text: <>Show me the UBO chain for Revolut, who’s the ultimate parent?</> },
      { text: <>Show me all the companies that Nikolay Storonsky owns</> },
      { text: <>Find top foreign owned companies in UK by revenue</> },
    ],
  },
];

export const McpExamplePromptsSection = () => {
  return (
    <Section
      title="Ask in plain language. Watch it chain tools."
      description="No query syntax to learn. Paste any of these into your connected assistant and it picks the right tools on its own."
    >
      <div className={styles.columns}>
        {COLUMNS.map((column) => (
          <div key={column.key} className={cn(styles.column, styles[`column_${column.key}`])}>
            <div className={styles.header}>
              <span className={styles.dot} />
              {column.label}
            </div>

            <div className={styles.cards}>
              {column.prompts.map((prompt, idx) => (
                <div key={idx} className={cn(styles.card, prompt.chain && styles.cardFeatured)}>
                  <div className={styles.cardRow}>
                    <span className={styles.prefix} aria-hidden="true">
                      &gt;
                    </span>

                    <span className={styles.promptText}>{prompt.text}</span>

                    <span className={styles.run} aria-hidden="true">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path
                          d="M2 8L8 2M8 2H3M8 2V7"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>

                  {prompt.chain && (
                    <div className={styles.response}>
                      <div className={styles.chain}>
                        {prompt.chain.map((tool, i) => (
                          <span key={tool} className={styles.chainStep}>
                            {i > 0 && (
                              <span className={styles.chainArrow} aria-hidden="true">
                                →
                              </span>
                            )}
                            {tool}
                          </span>
                        ))}
                      </div>

                      {prompt.answer && (
                        <p className={styles.answer}>
                          {prompt.answer}
                          <span className={styles.cursor} aria-hidden="true" />
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};
